import styled from '@emotion/styled';
import { Body, Box, HStack, Icon, VStack } from '@hover/blueprint';
import { Collapse, SlideFade } from '@hover/blueprint/chakra';
import { iChevronDown, iChevronUp } from '@hover/icons';
import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import { TranslationNamespace } from 'src/i18n/types';
import type { FeaturedProduct } from 'src/modules/renderings/components/RenderingsHub/FeaturedProduct/FeaturedProductCard';
import { FeaturedProductCard } from 'src/modules/renderings/components/RenderingsHub/FeaturedProduct/FeaturedProductCard';
import { FeaturedProductThumbnail } from 'src/modules/renderings/components/RenderingsHub/FeaturedProduct/FeaturedProductThumbnail';
import { TrayNavigationButton } from 'src/modules/renderings/components/RenderingsHub/FeaturedProduct/TrayNavigationButton';
import {
  useIsMobileSize,
  useRenderingContext,
} from 'src/modules/renderings/hooks';
import { useCurrentPage, useTranslate } from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';

import { DEFAULT_WIDTH, SMALL_WIDTH } from './FeaturedProductCard';
import { PaginationDots } from './PaginationDots';

const ANIMATION_CONFIG = {
  duration: 0.35,
  ease: 'easeInOut',
};

const ANIMATION_WITH_DELAY_CONFIG = {
  ...ANIMATION_CONFIG,
  delay: 0.25,
};

const NoScrollbarContainer = styled(HStack)`
  overflow: -moz-scrollbars-none;
  scrollbar-width: none;
  -ms-overflow-style: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const PillBox = styled(Box)`
  transition: opacity 0.35s ease-in-out;

  &.opacity-0 {
    opacity: 0;
  }
`;

type FeaturedProductTrayProps = {
  featuredProducts: FeaturedProduct[];
  testId?: string;
};

const FeaturedProductTray = ({
  featuredProducts,
  testId,
}: FeaturedProductTrayProps) => {
  const { trackingData } = useRenderingContext();
  const currentPage = useCurrentPage();
  const productTrayRef = useRef<HTMLDivElement>(null);
  const t = useTranslate(TranslationNamespace.SHARED);
  const [isOpen, setIsOpen] = useState(true);
  const [fromLeft, setFromLeft] = useState(0);
  const [fromRight, setFromRight] = useState(1);
  const isMobileSize = useIsMobileSize();
  const cardSize = isMobileSize ? 'small' : 'default';

  // If the screen size is mobile, always display the tray as open
  const shouldDisplayAsOpen = isMobileSize || isOpen;

  const hideShowText = isOpen
    ? t('renderings.featuredProducts.tray.hideProducts', {
        number: featuredProducts.length,
      })
    : t('renderings.featuredProducts.tray.showProducts', {
        number: featuredProducts.length,
      });

  const handleButtonScroll = (direction: 'left' | 'right') => () => {
    if (productTrayRef.current) {
      const scrollDistance =
        cardSize === 'default' ? DEFAULT_WIDTH : SMALL_WIDTH;
      if (direction === 'left') {
        productTrayRef.current.scrollLeft -= scrollDistance;
      } else {
        productTrayRef.current.scrollLeft += scrollDistance;
      }
    }
  };

  const totalPillImgs = 3;
  const pillFeaturedProducts = featuredProducts.slice(0, totalPillImgs);
  const numberRemaining = featuredProducts.length - totalPillImgs;

  const handleScroll = (event: React.UIEvent<ReactNode>) => {
    const { scrollLeft, scrollWidth, clientWidth } =
      event.target as HTMLDivElement;
    setFromLeft(scrollLeft);
    const distanceFromRight = scrollWidth - clientWidth - scrollLeft;
    setFromRight(distanceFromRight > 0 ? distanceFromRight : 0);
  };

  const handleToggleTray = () => {
    buttonPressed({
      ...trackingData,
      track_id: `rendering-product-tray-${isOpen ? 'hide' : 'show'}`,
      button_text: hideShowText,
      primary_cta: false,
      page_or_screen_name: currentPage || 'rendering-hub',
    });
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (productTrayRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = productTrayRef.current;
      const right = scrollWidth - (clientWidth + scrollLeft);
      setFromRight(right > 0 ? right : 0);
    }
  }, [productTrayRef.current?.scrollWidth, featuredProducts]);

  if (featuredProducts.length === 0) {
    return null;
  }

  return (
    <Box
      backgroundColor={{ base: 'neutral.0', tablet: 'neutral.100' }}
      borderBottomRadius={{ base: undefined, desktop: '4px' }}
      marginBottom={{ base: 500, tablet: 0 }}
      position="relative"
      testId={testId}
    >
      <VStack spacing={0} width="100%">
        <Box marginTop={shouldDisplayAsOpen ? '-20px' : 'unset'} width="100%">
          <Collapse
            in={shouldDisplayAsOpen}
            transition={{
              enter: ANIMATION_CONFIG,
              exit: ANIMATION_WITH_DELAY_CONFIG,
            }}
          >
            <SlideFade
              in={shouldDisplayAsOpen}
              offsetY="24px"
              transition={{
                enter: ANIMATION_WITH_DELAY_CONFIG,
                exit: ANIMATION_CONFIG,
              }}
            >
              <NoScrollbarContainer
                boxSizing="border-box"
                onScroll={handleScroll}
                overflowX="scroll"
                paddingBottom={200}
                paddingX={500}
                ref={productTrayRef}
                scrollBehavior="smooth"
                scrollPaddingLeft={{ base: 0, tablet: 500 }}
                scrollSnapType="x mandatory"
                spacing={500}
                width="100%"
              >
                {featuredProducts.map(featuredProduct => (
                  <FeaturedProductCard
                    cardSize={cardSize}
                    featuredProduct={featuredProduct}
                    key={featuredProduct.id}
                  />
                ))}
              </NoScrollbarContainer>
            </SlideFade>
          </Collapse>
        </Box>
        <Box
          cursor="pointer"
          display={{ base: 'none', tablet: 'flex' }}
          justifyContent="center"
          onClick={handleToggleTray}
          width="100%"
        >
          <PillBox
            alignItems="center"
            background="neutral.0"
            borderRadius="round"
            className={isOpen ? 'opacity-0' : ''}
            paddingX={400}
            paddingY={50}
            position="absolute"
            shadow="distance500"
            top="-16px"
            width="fit-content"
          >
            {pillFeaturedProducts.map((featuredProduct, index) => (
              <FeaturedProductThumbnail
                borderColor="neutral.0"
                borderRadius={400}
                borderStyle="solid"
                borderWidth="3px"
                featuredProduct={featuredProduct}
                imageSize="28px"
                isPill
                key={featuredProduct.id}
                style={{
                  marginLeft: index > 0 ? '-16px' : 'unset',
                  zIndex: totalPillImgs - index,
                }}
              />
            ))}
            {numberRemaining > 0 && (
              <Body marginLeft={200} marginY={0} size={400}>
                + {numberRemaining}
              </Body>
            )}
          </PillBox>
          <Box
            alignItems="center"
            marginBottom={400}
            marginTop={isOpen ? 300 : 500}
          >
            <Body
              alignSelf="center"
              color="neutral.600"
              fontWeight={700}
              isBold
              margin={0}
              padding={0}
              size={300}
              textTransform="uppercase"
            >
              {hideShowText}
            </Body>
            <Icon icon={isOpen ? iChevronUp : iChevronDown} />
          </Box>
        </Box>
      </VStack>
      <TrayNavigationButton
        direction="left"
        hideButton={fromLeft === 0}
        isOpen={isOpen}
        onClick={handleButtonScroll('left')}
      />
      <TrayNavigationButton
        direction="right"
        hideButton={fromRight === 0}
        isOpen={isOpen}
        onClick={handleButtonScroll('right')}
      />
      {isMobileSize && featuredProducts.length > 1 && (
        <PaginationDots
          scrollContainerRef={productTrayRef}
          scrollPosition={fromLeft}
          totalItems={featuredProducts.length}
        />
      )}
    </Box>
  );
};

export { FeaturedProductTray };
